import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Gavel,
  UserCheck,
  ClipboardList,
  ArrowRight,
  Scale
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

/**
 * Pre-Contract Compliance Landing Page
 */
const PreContractLanding = () => {
  const navigate = useNavigate();

  const complianceModules = [
    {
      id: 'compliance-policies',
      title: 'Compliance Policies',
      description:
        'Define and manage policy rules such as value thresholds and required clauses.',
      icon: <Gavel size={24} />,
      path: '/contracts/pre-contract/policies',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: [
        'Policy List',
        'Rule Definition',
        'Threshold Management'
      ]
    },
    {
      id: 'doa-matrix',
      title: 'DoA Matrix',
      description:
        'Configure delegation of authority including role-based contract value limits.',
      icon: <Scale size={24} />,
      path: '/contracts/pre-contract/doa',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: [
        'Role Limits',
        'Currency Config',
        'Approval Levels'
      ]
    },
    {
      id: 'due-diligence-config',
      title: 'Due Diligence Config',
      description:
        'Configure screening checks for sanctions, watchlists, and manual verification.',
      icon: <UserCheck size={24} />,
      path: '/contracts/pre-contract/due-diligence/config',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: [
        'Vendor Screening',
        'Watchlist Settings',
        'Manual Flags'
      ]
    },
    {
      id: 'compliance-check-results',
      title: 'Compliance Check Logs',
      description:
        'Monitor aggregate results of pre-contract gates and failure reasons.',
      icon: <ClipboardList size={24} />,
      path: '/contracts/pre-contract/logs',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: [
        'Pass/Fail Status',
        'Audit Logs',
        'Waiver Tracking'
      ]
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Pre-Contract Compliance"
        description="Enforce pre-signature compliance gates including policy, DoA, and due diligence checks."
      />

      <div className="page-container">
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {complianceModules.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => navigate(card.path)}
            >
              <div
                style={{
                  height: '4px',
                  background: card.iconLine,
                  width: '100%'
                }}
              />

              <div style={{ padding: '24px' }}>
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: card.iconBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div
                      style={{ color: card.iconLine }}
                      className="asset-card-icon"
                    >
                      {card.icon}
                    </div>
                  </div>

                  <div className="flex-grow-1">
                    <h4
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        margin: 0
                      }}
                    >
                      {card.title}
                    </h4>
                  </div>

                  <ArrowRight size={20} className="asset-card-arrow" />
                </div>

                <p
                  style={{
                    fontSize: '13px',
                    margin: '0 0 12px 0',
                    lineHeight: '1.4'
                  }}
                >
                  {card.description}
                </p>

                <div>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: '500',
                      marginBottom: '6px',
                      textTransform: 'uppercase'
                    }}
                  >
                    Sub-modules
                  </div>

                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default PreContractLanding;
